import { useMemo, useState } from 'react';
import { PROGRAMS } from '../../../constants/programs';
import useApi from '../../../hooks/useApi';
import EmailSendManualDialog from '../../emailAlerts/EmailSendManualDialog';

// This hook exposes the "manual email send" extension action used by reports
// that operate on email-send history or trigger workflows.
const useEmailSendManualAction = ({ enabled, onSent, onError }) => {
  const { useSendManualEmailTrigger, useEmailTriggers } = useApi();
  const sendManualEmail = useSendManualEmailTrigger();
  const { data: triggerData, isLoading: triggerLoading } = useEmailTriggers();

  const [open, setOpen] = useState(false);
  const [bac, setBac] = useState('');
  const [programName, setProgramName] = useState('');
  const [triggerId, setTriggerId] = useState('');
  const [error, setError] = useState('');

  const action = useMemo(() => {
    if (!enabled) return null;
    return { type: 'email_send_manual', params: { label: 'Add email' }, source: 'extension' };
  }, [enabled]);

  const allTriggers = Array.isArray(triggerData) ? triggerData : [];
  const triggerOptions = allTriggers
    .filter((trigger) => trigger?.isActive !== false)
    .filter((trigger) => !programName || !trigger?.programName || trigger.programName === programName)
    .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
  const programOptions = PROGRAMS.map((program) => ({ value: program, label: program }));

  const closeDialog = () => {
    setOpen(false);
    setBac('');
    setProgramName('');
    setTriggerId('');
    setError('');
  };

  const submit = async () => {
    if (!bac || !programName || !triggerId) return;
    setError('');
    try {
      await sendManualEmail.mutateAsync({ bac, programName, triggerId });
      closeDialog();
      if (onSent) onSent();
    } catch (err) {
      const message = err?.message || 'Failed to send manual email.';
      setError(message);
      if (onError) onError(message);
    }
  };

  const handleAction = (nextAction) => {
    if (nextAction?.type !== 'email_send_manual') return false;
    setError('');
    setOpen(true);
    return true;
  };

  const dialog = (
    <EmailSendManualDialog
      open={open}
      bac={bac}
      programName={programName}
      triggerId={triggerId}
      triggerOptions={triggerOptions}
      programOptions={programOptions}
      loadingTriggers={triggerLoading}
      submitting={sendManualEmail.isPending}
      error={error}
      onBacChange={setBac}
      onProgramChange={(value) => {
        setProgramName(value);
        setTriggerId('');
      }}
      onTriggerChange={setTriggerId}
      onClose={closeDialog}
      onSubmit={submit}
    />
  );

  return {
    action,
    dialog,
    handleAction,
  };
};

export default useEmailSendManualAction;
